import React, { useContext, useEffect, useState } from "react";

import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextInput,
  QuoteUrgency,
} from "@portex-pro/ui-components";
import Text from "components/Text";
import YesNoRadio from "components/YesNoRadio";
import { CargoGroupContext } from "features/cargo-groups/CargoGroupProvider";
import ContainerCargoGroups from "features/cargo-groups/ContainerCargoGroups";
import { DateTime } from "luxon";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import StepCollapse from "../../_components/StepCollapse";
import { useCreateStepCollapseProps } from "../../_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";

interface ShipmentDetailsStepTagProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cargoGroups?: any;
  poReferenceNumber?: string;
  isHazardous?: boolean;
}

const ShipmentDetailsStepTag: React.FC<ShipmentDetailsStepTagProps> = ({
  cargoGroups,
  poReferenceNumber,
  isHazardous,
}) => {
  const parts = [];

  if (cargoGroups?.length) {
    const firstContainer = cargoGroups[0];
    const commodities = `${firstContainer?.commodities}`;

    const commodityList = commodities
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    if (commodityList.length > 0) {
      const displayCommodities = commodityList.slice(0, 2);
      const excessCount = commodityList.length - 2;

      let commodityText = displayCommodities.join(", ");
      if (excessCount > 0) {
        commodityText += ` +${excessCount} more`;
      }
      parts.push(commodityText);
    }
  }

  if (isHazardous) {
    parts.push("Hazardous");
  }

  const poText = poReferenceNumber ? `PO# ${poReferenceNumber}` : null;

  if (parts.length === 0 && !poText) {
    return null;
  }

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      {parts.length > 0 && <span className="text-blue-600 font-bold text-sm">{parts.join(" • ")}</span>}
      {poText && <span className="text-blue-600 font-normal text-sm">&nbsp;{poText}</span>}
    </div>
  );
};

export const ShipmentDetailsStep: React.FC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["common", "shipper", "requestManagement"]);
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const { stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const [selectedUrgency, setSelectedUrgency] = useState<string | undefined>();
  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Shipment,
    name: t("shipper:shipmentDetails"),
    number: 2,
  });
  const { isValid } = useContext(CargoGroupContext);

  const methods = useFormContext<FinalSchema>();

  useEffect(() => {
    if (isValid) {
      methods.clearErrors("cargo_groups");
    } else {
      methods.setError("cargo_groups", { message: "Cargo group missing information" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const is_hazardous = useWatch({ control: methods.control, name: "is_hazardous" });
  const cargoGroups = useWatch({ control: methods.control, name: "cargo_groups" });
  const poReferenceNumber = useWatch({ control: methods.control, name: "reference_number" });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Partners);
    setIsOpen(false);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={
        <ShipmentDetailsStepTag
          cargoGroups={cargoGroups}
          poReferenceNumber={poReferenceNumber}
          isHazardous={is_hazardous}
        />
      }
      footer={
        stepIndex <= QuoteStepIndex.Shipment ? (
          <div className="flex justify-between mt-2 w-full">
            <div />
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 150 }}
            >
              {t("common:next")}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="p-6 grid grid-cols-4 gap-x-3 gap-y-6">
        <div className="col-span-4">
          <Text size="medium" weight="bold" typographyProps={{ color: "textSecondary" }}>
            {t("requestManagement:referenceNoAndUrgency")}
          </Text>
        </div>

        <Controller
          control={methods.control}
          name="reference_number"
          render={({ field }) => (
            <FormControl>
              <FormLabel>{t("requestManagement:poReferenceNumberOptional")}</FormLabel>
              <TextInput
                placeholder={t("requestManagement:poReferenceNumber_placeholder")}
                value={field.value}
                onChange={(event) => setQuoteRequestAction({ reference_number: event.target.value })}
                fullWidth
              />
            </FormControl>
          )}
        />

        <Controller
          control={methods.control}
          name="internal_reference_number"
          render={({ field }) => (
            <FormControl>
              <FormLabel>{t("shipper:shipmentDetailsStep.internalReferenceNumber")}</FormLabel>
              <TextInput
                value={field.value ?? ""}
                onChange={(event) => setQuoteRequestAction({ internal_reference_number: event.target.value })}
                fullWidth
              />
            </FormControl>
          )}
        />

        <Controller
          control={methods.control}
          name="hide_internal_ref_till_dispatch"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    setQuoteRequestAction({ hide_internal_ref_till_dispatch: e.target.checked });
                  }}
                />
              }
              label={t("shipper:shipmentDetailsStep.hideInternalRefTillDispatch")}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="deadline_respond_at"
          render={() => (
            <FormControl>
              <FormLabel>{t("requestManagement:urgency")}</FormLabel>
              <QuoteUrgency
                data={[
                  { id: "1", label: t("requestManagement:hours", { count: 1 }) },
                  { id: "2", label: t("requestManagement:hours", { count: 2 }) },
                  { id: "3", label: t("requestManagement:hours", { count: 3 }) },
                  { id: "6", label: t("requestManagement:hours", { count: 6 }) },
                  { id: "12", label: t("requestManagement:hours", { count: 12 }) },
                ]}
                value={selectedUrgency}
                style={{ height: "100%" }}
                getLabel={selectedUrgency ? (id) => t("requestManagement:hours", { count: Number(id) }) : undefined}
                onClear={() => {
                  setQuoteRequestAction({ deadline_respond_at: null });
                  setSelectedUrgency(undefined);
                }}
                onChange={(id) => {
                  setQuoteRequestAction({
                    deadline_respond_at: DateTime.now()
                      .plus({ hours: Number(id) })
                      .toISO(),
                  });
                  setSelectedUrgency(id);
                }}
              />
            </FormControl>
          )}
        />

        <div className="col-span-2" />

        <div className="col-span-4">
          <Text size="medium" weight="bold" typographyProps={{ color: "textSecondary" }}>
            {t("common:containers")}
          </Text>
        </div>

        <div className="col-span-3">
          <ContainerCargoGroups mode="tab" showError={!!methods.formState.errors.cargo_groups} />
        </div>

        <div className="col-span-1" />

        <div className="col-span-4">
          <Text size="medium" weight="bold" typographyProps={{ color: "textSecondary" }}>
            {t("common:commodities")}
          </Text>
        </div>

        <Controller
          control={methods.control}
          name="is_hazardous"
          render={({ field }) => (
            <FormControl fullWidth>
              <FormLabel>{t("requestManagement:doesThisContainerHazardousGoods")}</FormLabel>
              <YesNoRadio value={field.value} onChange={(value) => setQuoteRequestAction({ is_hazardous: value })} />
            </FormControl>
          )}
        />

        <Controller
          control={methods.control}
          name="include_cargo_insurance"
          render={({ field }) => (
            <FormControl fullWidth>
              <FormLabel>{t("requestManagement:doYouNeedCargoInsurance")}</FormLabel>
              <YesNoRadio
                value={field.value}
                onChange={(value) => setQuoteRequestAction({ insurance_required: value })}
              />
            </FormControl>
          )}
        />

        <Controller
          control={methods.control}
          name="is_overweight"
          render={({ field }) => (
            <FormControl fullWidth>
              <FormLabel>{t("shipper:shipmentDetailsStep.overweightLabel")}</FormLabel>
              <YesNoRadio value={field.value} onChange={(value) => setQuoteRequestAction({ is_overweight: value })} />
            </FormControl>
          )}
        />

        <Controller
          control={methods.control}
          name="include_customs_clearance"
          render={({ field }) => (
            <FormControl fullWidth>
              <FormLabel>{t("requestManagement:shouldCustomsClearanceBeIncluded")}</FormLabel>
              <YesNoRadio
                value={field.value}
                onChange={(value) => setQuoteRequestAction({ include_customs_clearance: value })}
              />
            </FormControl>
          )}
        />

        {is_hazardous && (
          <>
            <div className="col-span-3">
              <Controller
                control={methods.control}
                name="hazardous_notes"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <FormLabel>{t("requestManagement:describeTheHazardousGoods")}</FormLabel>
                    <TextInput
                      value={field.value}
                      multiline
                      rows={3}
                      onChange={(event) => setQuoteRequestAction({ hazardous_goods_details: event.target.value })}
                    />
                  </FormControl>
                )}
              />
            </div>
            <div />
          </>
        )}

        <div className="col-span-3">
          <Controller
            control={methods.control}
            name="notes"
            render={({ field }) => (
              <FormControl fullWidth>
                <FormLabel>{t("requestManagement:additionalNotes")}</FormLabel>
                <TextInput
                  value={field.value}
                  placeholder={t("requestManagement:additionalNotes_shipmentDetailsPlaceholder")}
                  onChange={(event) => setQuoteRequestAction({ note: event.target.value })}
                  multiline
                  rows={3}
                />
              </FormControl>
            )}
          />
        </div>

        <div />
      </div>
    </StepCollapse>
  );
};

export default ShipmentDetailsStep;
